import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import EngineerLayout from "../components/EngineerLayout";
import { appendAuthToken } from "../utils/auth";
import PhoneActions from "../components/PhoneActions";
import { formatDisplayDate } from "../components/SerialProductDetails";

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";

const createEmptyForm = () => ({
  customerId: "",
  testedOn: "",
  dueDate: "",
  entries: [
    {
      earthPitNo: "",
      individualResistance: "",
      combinedResistance: "",
      additionalInfo: ""
    }
  ]
});

const formatAddress = (address) => {
  if (!address) return "-";

  if (typeof address === "string") {
    return address || "-";
  }

  return [
    address.street,
    address.area,
    address.city,
    address.state,
    address.pincode
  ]
    .filter(Boolean)
    .join(", ") || "-";
};

const calculateDueDateValue = (value) => {
  if (!value) return "";

  const testedOnDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(testedOnDate.getTime())) return "";

  testedOnDate.setFullYear(testedOnDate.getFullYear() + 1);
  testedOnDate.setDate(testedOnDate.getDate() - 1);

  const year = testedOnDate.getFullYear();
  const month = String(testedOnDate.getMonth() + 1).padStart(2, "0");
  const day = String(testedOnDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function EarthPit() {
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;

  const [customers, setCustomers] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(createEmptyForm);
  const [customerSearch, setCustomerSearch] = useState("");
  const [historySort, setHistorySort] = useState("testedOn-desc");
  const [historyFilterDate, setHistoryFilterDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const buildEarthPitPdfUrl = (customerId) => {
    if (!customerId) return "";

    const params = new URLSearchParams();
    if (historyFilterDate) {
      params.set("date", historyFilterDate);
    }

    const query = params.toString();
    return API_BASE_URL + `/api/earth-pits/customer/${customerId}/pdf${query ? `?${query}` : ""}`;
  };

  const openCustomerEarthPitPdf = (customerId) => {
    if (!customerId) return;
    window.open(appendAuthToken(buildEarthPitPdfUrl(customerId)), "_blank", "noopener,noreferrer");
  };

  const generateCustomerEarthPitPdf = (customerId) => {
    if (!customerId) return;

    const link = document.createElement("a");
    const params = new URLSearchParams();
    if (historyFilterDate) {
      params.set("date", historyFilterDate);
    }
    params.set("download", "1");
    link.href = appendAuthToken(API_BASE_URL + `/api/earth-pits/customer/${customerId}/pdf?${params.toString()}`);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [customersRes, recordsRes] = await Promise.all([
          axios.get(API_BASE_URL + "/api/customers"),
          axios.get(API_BASE_URL + "/api/earth-pits")
        ]);

        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        setRecords(Array.isArray(recordsRes.data) ? recordsRes.data : []);
      } catch (error) {
        console.error("Failed to load earth pit data", error);
        setCustomers([]);
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [engineerLookup]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer._id) === form.customerId) || null,
    [customers, form.customerId]
  );

  const filteredCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();

    return customers.filter((customer) =>
      [
        customer.customerId,
        customer.name,
        customer.contactPerson,
        customer.phone,
        formatAddress(customer.address)
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [customerSearch, customers]);

  const customerRecords = useMemo(() => {
    const filtered = records
      .filter((record) => String(record.customerId) === form.customerId)
      .filter((record) => {
        if (!historyFilterDate) return true;

        const testedOn = record?.testedOn ? new Date(record.testedOn) : null;
        if (!testedOn || Number.isNaN(testedOn.getTime())) return false;

        const year = testedOn.getFullYear();
        const month = String(testedOn.getMonth() + 1).padStart(2, "0");
        const day = String(testedOn.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}` === historyFilterDate;
      });

    const sorted = [...filtered];
    switch (historySort) {
      case "testedOn-asc":
        sorted.sort((a, b) => new Date(a.testedOn || 0) - new Date(b.testedOn || 0));
        break;
      case "earthPitNo-asc":
        sorted.sort((a, b) => String(a.earthPitNo || "").localeCompare(String(b.earthPitNo || ""), undefined, { numeric: true, sensitivity: "base" }));
        break;
      case "earthPitNo-desc":
        sorted.sort((a, b) => String(b.earthPitNo || "").localeCompare(String(a.earthPitNo || ""), undefined, { numeric: true, sensitivity: "base" }));
        break;
      case "testedOn-desc":
      default:
        sorted.sort((a, b) => new Date(b.testedOn || 0) - new Date(a.testedOn || 0));
        break;
    }

    return sorted;
  }, [records, form.customerId, historySort, historyFilterDate]);

  const handleEntryChange = (index, name, value) => {
    setForm((current) => ({
      ...current,
      entries: current.entries.map((entry, entryIndex) =>
        entryIndex !== index
          ? entry
          : {
              ...entry,
              [name]: value
            }
      )
    }));
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "testedOn" ? { dueDate: calculateDueDateValue(value) } : {})
    }));
  };

  const handleCustomerSelect = (customerId) => {
    setForm((current) => ({
      ...current,
      customerId: String(customerId || "")
    }));
  };

  const addEntryRow = () => {
    setForm((current) => ({
      ...current,
      entries: [
        ...current.entries,
        {
          earthPitNo: "",
          individualResistance: "",
          combinedResistance: "",
          additionalInfo: ""
        }
      ]
    }));
  };

  const removeEntryRow = (index) => {
    setForm((current) => ({
      ...current,
      entries:
        current.entries.length === 1
          ? current.entries
          : current.entries.filter((_, entryIndex) => entryIndex !== index)
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const payload = {
        engineerId: engineerLookup,
        engineerName,
        customerId: form.customerId,
        testedOn: form.testedOn,
        entries: form.entries.map((entry) => ({
          earthPitNo: entry.earthPitNo,
          individualResistance: entry.individualResistance,
          combinedResistance: entry.combinedResistance,
          additionalInfo: entry.additionalInfo
        }))
      };

      const response = await axios.post(API_BASE_URL + "/api/earth-pits", payload);

      const savedEntries = Array.isArray(response.data) ? response.data : [response.data];
      setRecords((current) => [...savedEntries, ...current]);
      setForm((current) => ({
        ...createEmptyForm(),
        customerId: current.customerId
      }));
      alert("Earth pit details saved successfully.");
    } catch (error) {
      console.error("Failed to save earth pit", error);
      alert(error.response?.data?.message || "Failed to save earth pit details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <EngineerLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.95))] p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-700">
            Engineer Testing
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Earth Pit</h1>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.3fr_1fr]">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Search Customer</label>
              <input
                type="text"
                value={customerSearch}
                onChange={(event) => setCustomerSearch(event.target.value)}
                className={inputClassName}
                placeholder="Search by customer name, ID, contact, phone..."
              />
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Engineer</p>
              <p className="mt-2 text-sm font-medium text-slate-800">{engineerName || engineerLookup || "-"}</p>
            </div>
          </div>

          <div className="mt-5 max-h-72 overflow-y-auto rounded-3xl border border-slate-200 bg-slate-50 p-3">
            {loading ? (
              <div className="rounded-2xl bg-white px-4 py-5 text-center text-sm text-slate-500">
                Loading customers...
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="rounded-2xl bg-white px-4 py-5 text-center text-sm text-slate-500">
                No customers found.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCustomers.map((customer) => {
                  const isActive = String(customer._id) === form.customerId;

                  return (
                    <button
                      key={customer._id}
                      type="button"
                      onClick={() => handleCustomerSelect(customer._id)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                        isActive
                          ? "border-sky-500 bg-sky-50"
                          : "border-slate-200 bg-white hover:border-sky-300 hover:bg-sky-50/40"
                      }`}
                    >
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{customer.name || "-"}</p>
                          <p className="text-xs text-slate-500">
                            {customer.customerId || "Customer"} • {customer.contactPerson || customer.phone || "-"}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500">{formatAddress(customer.address)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {selectedCustomer && (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Customer Details</h2>
              </div>
              <div className="rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700">
                {selectedCustomer.customerId || "Customer"}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InfoCard label="Customer Name" value={selectedCustomer.name || "-"} />
              <InfoCard label="Contact Person" value={selectedCustomer.contactPerson || "-"} />
              <InfoCard label="Phone" value={<PhoneActions phone={selectedCustomer.phone || ""} />} />
              <InfoCard label="Email" value={selectedCustomer.email || "-"} />
              <InfoCard label="Address" value={formatAddress(selectedCustomer.address)} />
              <InfoCard label="Additional Info" value={selectedCustomer.additionalInfo || "-"} />
            </div>
          </section>
        )}

        <form onSubmit={handleSubmit} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Earth Pit Entry</h2>
            </div>
            <button
              type="button"
              onClick={addEntryRow}
              className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              + Add Earth Pit
            </button>
          </div>

          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
            Specified Value: Individual - 5 Ohms, Combined - 2 Ohms
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Tested On</label>
              <input
                type="date"
                name="testedOn"
                value={form.testedOn}
                onChange={handleFieldChange}
                className={inputClassName}
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={form.dueDate}
                className={`${inputClassName} bg-slate-50`}
                readOnly
              />
            </div>
          </div>

          <div className="mt-5 overflow-x-auto rounded-3xl border border-slate-200">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-4 py-3 font-semibold">Earth Pit No</th>
                  <th className="px-4 py-3 font-semibold">Individual Resistance (Ohm)</th>
                  <th className="px-4 py-3 font-semibold">Combined Resistance (Ohm)</th>
                  <th className="px-4 py-3 font-semibold">Additional Information</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {form.entries.map((entry, index) => (
                  <tr key={`earth-pit-entry-${index}`} className="border-t border-slate-200">
                    <td className="px-3 py-3">
                      <input
                        value={entry.earthPitNo}
                        onChange={(event) => handleEntryChange(index, "earthPitNo", event.target.value)}
                        className={inputClassName}
                        placeholder="Earth pit no"
                        required
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        value={entry.individualResistance}
                        onChange={(event) => handleEntryChange(index, "individualResistance", event.target.value)}
                        className={inputClassName}
                        placeholder="Individual resistance"
                        required
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        value={entry.combinedResistance}
                        onChange={(event) => handleEntryChange(index, "combinedResistance", event.target.value)}
                        className={inputClassName}
                        placeholder="Combined resistance"
                        required
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        value={entry.additionalInfo || ""}
                        onChange={(event) => handleEntryChange(index, "additionalInfo", event.target.value)}
                        className={inputClassName}
                        placeholder="Additional information"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => removeEntryRow(index)}
                        disabled={form.entries.length === 1}
                        className="rounded-2xl bg-red-100 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={!form.customerId || saving}
              className="rounded-2xl bg-sky-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {saving ? "Saving..." : "Save Earth Pit"}
            </button>
          </div>
        </form>

        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Earth Pit History</h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {form.customerId && customerRecords.length > 0 && (
                <input
                  type="date"
                  value={historyFilterDate}
                  onChange={(event) => setHistoryFilterDate(event.target.value)}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                />
              )}
              {form.customerId && customerRecords.length > 0 && (
                <select
                  value={historySort}
                  onChange={(event) => setHistorySort(event.target.value)}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                >
                  <option value="testedOn-desc">Latest Tested On</option>
                  <option value="testedOn-asc">Oldest Tested On</option>
                  <option value="earthPitNo-asc">Earth Pit No A-Z</option>
                  <option value="earthPitNo-desc">Earth Pit No Z-A</option>
                </select>
              )}
              {form.customerId && customerRecords.length > 0 && (
                <button
                  type="button"
                  onClick={() => openCustomerEarthPitPdf(form.customerId)}
                  className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
                >
                  Open PDF
                </button>
              )}
              {form.customerId && customerRecords.length > 0 && (
                <button
                  type="button"
                  onClick={() => generateCustomerEarthPitPdf(form.customerId)}
                  className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Generate PDF
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 px-5 py-10 text-center text-slate-500">
              Loading earth pit details...
            </div>
          ) : !form.customerId ? (
            <div className="mt-5 rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-slate-500">
              Select a customer to view earth pit entries.
            </div>
          ) : customerRecords.length === 0 ? (
            <div className="mt-5 rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-slate-500">
              No earth pit records found for this customer.
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-3xl border border-slate-200">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Earth Pit No</th>
                    <th className="px-4 py-3 font-semibold">Individual Resistance (Ohm)</th>
                    <th className="px-4 py-3 font-semibold">Combined Resistance (Ohm)</th>
                    <th className="px-4 py-3 font-semibold">Additional Information</th>
                    <th className="px-4 py-3 font-semibold">Tested On</th>
                    <th className="px-4 py-3 font-semibold">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {customerRecords.map((record) => (
                    <tr key={record._id} className="border-t border-slate-200">
                      <td className="px-4 py-3 font-medium text-slate-900">{record.earthPitNo || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{record.individualResistance || record.earthResistance || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{record.combinedResistance || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{record.additionalInfo || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDisplayDate(record.testedOn)}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDisplayDate(record.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </EngineerLayout>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className="mt-2 text-sm leading-6 text-slate-700 break-words">{value}</div>
    </div>
  );
}

export default EarthPit;


